import { useEffect } from 'react';
import { ArrowLeft, RotateCcw } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import {
  Button,
  ErrorMessage,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  OtpInput,
  Spinner,
  cn,
} from '@finmin/ui';
import type { LoginOtpValues, SignupOtpValues } from '../types';
import { maskPhone, formatCountdown } from '../utils/phone';
import { useWebOtp } from '../hooks/use-web-otp';

// Both login and signup OTP forms share the same shape
type OtpValues = LoginOtpValues | SignupOtpValues;

interface OtpStepProps {
  /** E.164 phone number sent the code — used for the masked display */
  mobile: string;
  /** Controlled OTP string coming from parent (kept in sync with RHF) */
  otpValue: string;
  setOtpValue: (value: string) => void;
  isPending: boolean;
  apiError: string | null;
  /** Seconds remaining on the resend cooldown */
  resendSecondsLeft: number;
  isResending: boolean;
  /** Label for the submit button, e.g. "Verify & Sign in" or "Verify & Create account" */
  submitLabel?: string;
  onSubmit: (values: OtpValues) => void;
  onResend: () => void;
  onBack: () => void;
}

/**
 * OTP verification step — shared by login and signup.
 * Caller wraps with <Form {...form}> and passes the form via context.
 */
export function OtpStep({
  mobile,
  otpValue,
  setOtpValue,
  isPending,
  apiError,
  resendSecondsLeft,
  isResending,
  submitLabel = 'Verify',
  onSubmit,
  onResend,
  onBack,
}: OtpStepProps) {
  const form = useFormContext<OtpValues>();
  // Pull out setValue — it is a stable function reference across renders.
  // Including the full `form` object in deps would cause an infinite loop
  // because useFormContext returns a new object reference on every render.
  const { setValue } = form;

  // Keep RHF value in sync with the controlled OTP input.
  // We only run validation once the user has filled all 6 digits — otherwise
  // a partial value (e.g. "1") trips the "must be 6 digits" rule and the
  // boxes flash red while the user is still typing.
  useEffect(() => {
    setValue('otp', otpValue, { shouldValidate: otpValue.length === 6 });
  }, [otpValue, setValue]);

  function handleOtpComplete(value: string) {
    form.setValue('otp', value);
    form.handleSubmit(onSubmit)();
  }

  // Android Chrome: auto-read the SMS code via WebOTP. Fill the boxes, and
  // auto-submit when we get a full 6-digit code. No-ops on unsupported
  // browsers (iOS Safari uses its native one-time-code suggestion instead).
  useWebOtp((code) => {
    const digits = code.replace(/\D/g, '').slice(0, 6);
    if (!digits) return;
    setOtpValue(digits);
    if (digits.length === 6) handleOtpComplete(digits);
  }, !isPending);

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      noValidate
      className="space-y-6 animate-step-in"
    >
      {/* Back navigation */}
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
      >
        <ArrowLeft
          className="h-4 w-4 transition-transform group-hover:-translate-x-0.5"
          aria-hidden
        />
        Back
      </button>

      {/* Heading */}
      <div className="space-y-1">
        <h2 className="font-display font-medium text-2xl tracking-tight text-foreground">
          Enter verification code.
        </h2>
        <p className="text-sm text-muted-foreground">
          We sent a 6-digit code to{' '}
          <span className="font-medium text-foreground">{maskPhone(mobile)}</span>.
        </p>
      </div>

      {/* OTP boxes */}
      <FormField
        control={form.control}
        name="otp"
        render={({ fieldState }) => {
          // Only paint the boxes red when:
          //   1. The user has submitted (or filled all 6 digits) AND validation failed, OR
          //   2. The API rejected the code.
          // While the user is still typing 1–5 digits, keep the boxes neutral so
          // they don't think they did something wrong on the very first keystroke.
          const showError =
            (otpValue.length === 6 && !!fieldState.error) || !!apiError;
          return (
            <FormItem>
              <FormLabel>
                Verification code{' '}
                <span className="text-destructive" aria-label="required">*</span>
              </FormLabel>
              <FormControl>
                <OtpInput
                  value={otpValue}
                  onChange={setOtpValue}
                  onComplete={handleOtpComplete}
                  disabled={isPending}
                  error={showError}
                  autoFocus
                />
              </FormControl>
              {/* Only surface the inline message text once the box state is
                  actually red — keeps the gap quiet while typing. */}
              {showError && <FormMessage />}
            </FormItem>
          );
        }}
      />

      {/* API-level error */}
      <ErrorMessage message={apiError} />

      {/* Resend row */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">Didn&apos;t receive a code?</span>
        {resendSecondsLeft > 0 ? (
          <span className="font-medium text-muted-foreground tabular-nums">
            Resend in {formatCountdown(resendSecondsLeft)}
          </span>
        ) : (
          <button
            type="button"
            onClick={onResend}
            disabled={isResending}
            className={cn(
              'inline-flex items-center gap-1.5 font-medium text-primary',
              'hover:underline underline-offset-4 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {isResending ? (
              <Spinner className="h-3.5 w-3.5" />
            ) : (
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            )}
            Resend OTP
          </button>
        )}
      </div>

      {/* Submit */}
      <Button
        type="submit"
        className="w-full gap-2"
        size="lg"
        disabled={isPending || otpValue.length < 6}
      >
        {isPending ? (
          <>
            <Spinner className="h-4 w-4" />
            Verifying…
          </>
        ) : (
          submitLabel
        )}
      </Button>
    </form>
  );
}
